import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	isCollectionFilterChangeEvent,
	publish,
	PUB_SUB_EVENTS,
	subscribe,
} from '@/scripts/core/global'
import { initializeScrollAnimationTrigger } from '@/scripts/theme/animations'

// Every live instance, including ones whose grid has already been replaced.
// A grid re-render builds a NEW <infinite-scroll> and leaves the previous one
// detached but still running: its appendProducts resolves #product-grid live, so
// it would write a stale ordering's page into the successor grid. The caller can
// only query the DOM for the current element, so it cannot reach the stale one --
// hence this registry.
const infiniteScrollInstances = new Set<InfiniteScroll>()

/** Stop every instance's load-through and make its in-flight fetches bail. */
export function invalidateAllInfiniteScroll(): void {
	for (const instance of infiniteScrollInstances) instance.invalidate()
}

export class InfiniteScroll extends UcoastEl {
	/**
	 * Bumped whenever the list is replaced (reset or a new render). A
	 * load-through captures it and abandons its work if it changes, so a sort
	 * landing mid-fetch cannot have a stale run append pages or race counters.
	 */
	private loadGeneration = 0
	// The grid this load-through is serving, and the query string it started
	// with. Both are captured once per run so a concurrent sort cannot change the
	// node we write to or the ordering we page through under our feet.
	private runGrid: HTMLElement | null = null
	private runSearch: string | null = null
	private loadThroughRunning = false
	private loadThroughPending = false

	static htmlSelector = 'infinite-scroll'

	private loadMoreButton: HTMLButtonElement | null = null
	private productGrid: HTMLElement | null = null
	private currentPage: number = 1
	private totalPages: number = 1
	private sectionId: string = ''
	private isLoading: boolean = false
	private filterActive: boolean = false

	constructor() {
		super()
		infiniteScrollInstances.add(this)
		this.init()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		// Drop out of the registry once this instance can no longer matter. It
		// must stay while a load-through is in flight: a detached instance that is
		// still writing is the case the registry exists to catch. Keeping every
		// instance forever would retain detached DOM for the life of the page, one
		// entry per grid render.
		if (!this.loadThroughRunning && !this.loadThroughPending) {
			infiniteScrollInstances.delete(this)
		}
	}

	private init() {
		// Get the load more button
		this.loadMoreButton = this.querySelector('button[data-load-more]')

		// Get product grid
		this.productGrid = document.querySelector('#product-grid')

		if (!this.loadMoreButton || !this.productGrid) {
			console.error('InfiniteScroll: Required elements not found')
			return
		}

		// Get initial data from attributes
		this.currentPage = parseInt(
			this.getAttribute('data-current-page') || '1'
		)
		this.totalPages = parseInt(this.getAttribute('data-total-pages') || '1')
		this.sectionId = this.getAttribute('data-section-id') || ''

		// Bind event listeners
		this.loadMoreButton.addEventListener('click', this.loadMore.bind(this))

		// Filtering is client-side, so a match on a later page stays invisible
		// until that page is in the grid. Suspend the observer -- a shortened
		// grid would otherwise retrigger it endlessly -- and load the rest of the
		// collection deliberately instead.
		subscribe(PUB_SUB_EVENTS.collectionFilterChange, (event) => {
			if (isCollectionFilterChangeEvent(event)) {
				this.filterActive = event.activeTag !== null
				if (this.filterActive) this.loadThrough()
			}
		})

		// Set up Intersection Observer for automatic loading
		this.setupIntersectionObserver()

		// Update button visibility
		this.updateButtonVisibility()
	}

	private setupIntersectionObserver() {
		if (!this.loadMoreButton) return

		// Create an Intersection Observer to watch when the button becomes visible
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					// If the button is visible and we're not already loading
					if (
						entry.isIntersecting &&
						!this.isLoading &&
						!this.filterActive &&
						this.currentPage < this.totalPages
					) {
						this.loadMore()
					}
				})
			},
			{
				// Trigger when 50% of the button is visible
				threshold: 0.5,
				// Add some margin to trigger slightly before the button is fully visible
				rootMargin: '100px',
			}
		)

		// Start observing the load more button
		observer.observe(this.loadMoreButton)
	}

	private async loadMore() {
		if (this.isLoading || this.currentPage >= this.totalPages) {
			return
		}

		const generation = this.loadGeneration
		const nextPage = this.currentPage + 1
		this.isLoading = true
		this.setLoadingState(true)

		try {
			const url = this.buildUrl(nextPage)

			const response = await fetch(url)
			const html = await response.text()

			// A sort or filter change since this request started has already
			// replaced the list and reset the counters, so this page belongs to
			// a grid that is no longer on the page.
			if (generation !== this.loadGeneration) {
				return
			}

			this.appendProducts(html)
			this.currentPage = nextPage
			this.updateButtonVisibility()

			// Initialize scroll animations for new products
			const newDocument = new DOMParser().parseFromString(
				html,
				'text/html'
			)
			if (typeof initializeScrollAnimationTrigger === 'function') {
				initializeScrollAnimationTrigger(newDocument.documentElement)
			}
		} catch (error) {
			console.error('InfiniteScroll: Error loading more products', error)
		} finally {
			this.isLoading = false
			this.setLoadingState(false)
		}
	}

	/**
	 * Load every remaining page so a client-side filter sees the whole
	 * collection. Bails out if the filter is cleared part-way through.
	 */
	private async loadThrough(): Promise<void> {
		// Applying the filter publishes collectionFilterChange, whose subscriber
		// calls loadThrough, and every appended page re-runs applyFilter through
		// the MutationObserver. Those two feed each other, so a nested run would
		// loop until the tab dies. Instead of nesting, note that more work is
		// wanted and let the run already in flight pick it up — that also covers
		// the run a sort wants, which a one-shot guard would have swallowed.
		if (this.loadThroughRunning) {
			this.loadThroughPending = true
			return
		}

		this.runGrid = document.querySelector('#product-grid')
		this.runSearch = window.location.search
		this.loadThroughRunning = true
		try {
			do {
				this.loadThroughPending = false
				const generation = this.loadGeneration
				while (
					this.filterActive &&
					!this.isLoading &&
					this.currentPage < this.totalPages &&
					generation === this.loadGeneration
				) {
					await this.loadMore()
				}
			} while (this.loadThroughPending)
		} finally {
			this.loadThroughRunning = false
			this.loadThroughPending = false
			// A finished run on an already-detached instance has nothing left to
			// guard, so stop retaining it.
			if (!this.isConnected) infiniteScrollInstances.delete(this)
			// The do/while above has drained any pending rerun by now, so this is the
			// point at which the grid has genuinely settled.
			publish(PUB_SUB_EVENTS.loadThroughEnd, undefined)
		}
	}

	private buildUrl(page: number): string {
		// Use the ordering captured at run start. Re-reading location.search here is
		// what let a mid-run sort make page 2 of a different ordering arrive into a
		// grid holding page 1 of the previous one.
		const searchParams = new URLSearchParams(
			this.runSearch ?? window.location.search
		)
		searchParams.set('page', page.toString())
		searchParams.set('section_id', this.sectionId)

		return `${window.location.pathname}?${searchParams.toString()}`
	}

	private appendProducts(html: string) {
		// The grid is rebuilt whenever the facets re-render, so resolve it now
		// rather than appending into the reference captured at setup (which
		// would be a detached node, and the products would never appear).
		const grid = document.querySelector<HTMLElement>('#product-grid')
		if (grid && this.runGrid && grid !== this.runGrid) {
			return
		}
		if (!grid) {
			return
		}
		this.productGrid = grid

		const parser = new DOMParser()
		const doc = parser.parseFromString(html, 'text/html')
		const newProductGrid = doc.querySelector('#product-grid')

		if (!newProductGrid) {
			console.error('InfiniteScroll: Product grid not found in response')
			return
		}

		// Get all product items (li elements with class grid__item)
		const newProducts = newProductGrid.querySelectorAll('li.grid__item')

		if (newProducts.length === 0) {
			console.warn('InfiniteScroll: No products found in response')
			return
		}

		// Append each product to the existing grid, skipping any already present.
		// A page can resolve after a sort or filter change has already rebuilt the
		// grid, and appending it blind duplicated products while the load-through
		// appeared to stall. The product handle is the stable identity.
		// Card hrefs differ between the full page ('/products/handle') and the
		// section endpoint ('/collections/<c>/products/handle', sometimes split
		// across source lines), so match the products/ segment wherever it sits.
		const handleOf = (element: Element): string | undefined =>
			element
				.querySelector('a[href*="/products/"]')
				?.getAttribute('href')
				?.match(/\/products\/([^/?#]+)/)?.[1]

		const seen = new Set<string>()
		grid.querySelectorAll('li.grid__item').forEach((item) => {
			const handle = handleOf(item)
			if (handle) seen.add(handle)
		})

		newProducts.forEach((product) => {
			const handle = handleOf(product)
			if (handle && seen.has(handle)) return
			if (handle) seen.add(handle)
			grid.appendChild(product.cloneNode(true))
		})
	}

	private setLoadingState(loading: boolean) {
		if (!this.loadMoreButton) return

		if (loading) {
			this.loadMoreButton.setAttribute('disabled', 'disabled')
			this.loadMoreButton.classList.add('loading')
		} else {
			this.loadMoreButton.removeAttribute('disabled')
			this.loadMoreButton.classList.remove('loading')
		}
	}

	private updateButtonVisibility() {
		if (!this.loadMoreButton) return

		if (this.currentPage >= this.totalPages) {
			this.loadMoreButton.style.display = 'none'
		} else {
			this.loadMoreButton.style.display = ''
		}
	}

	// Public method to reset infinite scroll (called when filters/sorting change)
	/**
	 * Stop this instance's load-through and make its in-flight fetches bail.
	 * Public because a grid re-render must invalidate instances it can no longer
	 * reach by querying the DOM.
	 */
	public invalidate(): void {
		this.loadGeneration++
		this.isLoading = false
		this.runGrid = null
	}

	public reset(currentPage: number = 1, totalPages: number = 1) {
		// Invalidate any load-through still in flight: its grid is gone.
		this.loadGeneration++
		this.currentPage = currentPage
		this.totalPages = totalPages
		this.isLoading = false
		this.setLoadingState(false)
		this.updateButtonVisibility()
	}

	// Update from new HTML (called after filter/sort)
	public updateFromHTML(html: string) {
		const parser = new DOMParser()
		const doc = parser.parseFromString(html, 'text/html')
		const newInfiniteScroll = doc.querySelector('infinite-scroll')

		if (newInfiniteScroll) {
			const newCurrentPage = parseInt(
				newInfiniteScroll.getAttribute('data-current-page') || '1'
			)
			const newTotalPages = parseInt(
				newInfiniteScroll.getAttribute('data-total-pages') || '1'
			)
			this.reset(newCurrentPage, newTotalPages)
		} else {
			// No infinite scroll in new HTML, hide button
			this.reset(1, 1)
		}
	}
}
